import {useRef, useState} from "react";
import {css, cva, cx} from "@panda/css";

import {BrandCellPhoneIcon, BrandCoinIcon, BrandTenementIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon} from "@pg-design/icons-module";
import {Modal} from "@pg-design/modal-module";
import {getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {useUserDevice} from "@pg-mono/user-device";

interface IProps {
    closeModal: () => void;
    isOpen: boolean;
}

type Slide = {
    title: string;
    body: React.ReactNode;
    leftBg: string;
    icon?: React.ElementType;
    size?: string;
};

const slides: Slide[] = [
    {
        title: "Wszystkie ceny ofert widoczne po raz pierwszy w jednym miejscu!",
        body: <>Dotąd część ofert ukrywała ceny. Teraz zobaczysz je wszystkie w jednym miejscu. Dzięki temu łatwiej porównasz oferty i zaplanujesz budżet.</>,
        leftBg: "var(--colors-info)",
        icon: BrandCoinIcon
    },
    {
        title: "Inteligentne rekomendacje",
        body: (
            <>
                Nasz system wyszuka i wybierze mieszkania dopasowane do Twoich potrzeb. To sprawi, że szybciej dotrzesz do ofert, które naprawdę Ci odpowiadają.
            </>
        ),
        leftBg: "var(--colors-warning)",
        icon: BrandTenementIcon
    },
    {
        title: "Twoje Konto",
        body: (
            <>
                By pomóc Ci w poszukiwaniach, stworzyliśmy dla Ciebie jedną przestrzeń, gdzie możesz wygodnie śledzić i porównywać oferty dopasowane do Twoich
                preferencji oraz komunikować się z deweloperami.
            </>
        ),
        leftBg: "var(--colors-highlight)",
        icon: BrandCellPhoneIcon
    }
];

export const PriceTransparencyModal = ({closeModal, isOpen}: IProps) => {
    const [idx, setIdx] = useState(0);
    const trackRef = useRef<HTMLDivElement | null>(null);
    const {isMobile} = useUserDevice();

    const goTo = (i: number) => {
        setIdx(i);
    };

    const prev = () => goTo((idx - 1 + slides.length) % slides.length);
    const next = () => goTo((idx + 1) % slides.length);

    const slide = slides[idx];

    return (
        <Modal isOpen={isOpen} onModalClose={closeModal} className={modalWrapper}>
            <Modal.CloseButton className={closeButton} placement="right" wrapperColor="#fff" size={isMobile ? "2" : "1.5"} />

            <div className={gridWrap}>
                <div className={leftPanel} style={{backgroundColor: slide.leftBg}}>
                    <div className={leftCol}>
                        <div className={sliderViewport}>
                            <div ref={trackRef} className={sliderTrack} style={{transform: `translateX(-${idx * 100}%)`}}>
                                {slides.map((item, i) => (
                                    <div key={i} className={slideItem}>
                                        <BrandArrowAddon direction="up-left" />

                                        <div className={modalContent}>
                                            {item.icon && (
                                                <item.icon
                                                    size={isMobile ? "5.7" : "9.4"}
                                                    wrapperSize={isMobile ? "5.7" : "9.4"}
                                                    wrapperColor={getThemeVariable("colors.primary")}
                                                />
                                            )}

                                            <Text variant="headline_1" align="center" className={css({maxWidth: "521px"})}>
                                                {item.title}
                                            </Text>

                                            <Text variant="headline_4" align="center" className={css({maxWidth: "628px"})}>
                                                {item.body}
                                            </Text>
                                        </div>

                                        <BrandArrowAddon direction="down-right" />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <ModalControls
                            dotSize={isMobile ? "small" : "medium"}
                            arrowSize={isMobile ? "1.5" : "2.5"}
                            prev={prev}
                            next={next}
                            idx={idx}
                            setIdx={goTo}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

interface IModalControlsProps {
    prev: () => void;
    next: () => void;
    idx: number;
    setIdx: (idx: number) => void;
    arrowSize: string;
    dotSize?: "small" | "medium";
}

const ModalControls = ({prev, next, idx, setIdx, arrowSize, dotSize = "medium"}: IModalControlsProps) => {
    return (
        <div className={controlsRow}>
            <button type="button" className={navBtn} onClick={prev} aria-label="Poprzedni">
                <ChevronLeftIcon size={arrowSize} fill="#000" wrapperColor="#fff" wrapperSize="5" />
            </button>

            <div className={css({display: "flex", gap: 16})}>
                {slides.map((_, i) => (
                    <button
                        key={i}
                        className={cx(dot({active: i === idx, dotSize: dotSize}))}
                        aria-label={`Przejdź do slajdu ${i + 1}`}
                        onClick={() => setIdx(i)}
                    />
                ))}
            </div>

            <button type="button" className={navBtn} onClick={next} aria-label="Następny">
                <ChevronRightIcon size={arrowSize} fill="#000" wrapperColor="#fff" wrapperSize="5" />
            </button>
        </div>
    );
};

const dot = cva({
    base: {
        borderRadius: "100%",
        backgroundColor: "var(--colors-gray-500)",
        cursor: "pointer"
    },
    variants: {
        active: {
            true: {
                backgroundColor: "var(--colors-primary)"
            }
        },
        dotSize: {
            small: {
                height: 10,
                width: 10
            },
            medium: {
                height: 16,
                width: 16
            }
        }
    }
});

const BrandArrowAddon = ({direction}: {direction: "up-left" | "down-right"}) => {
    const {isMobile} = useUserDevice();

    return (
        <>
            {direction === "up-left" && (
                <ChevronUpIcon
                    size={isMobile ? "9" : "16"}
                    wrapperSize={isMobile ? "9" : "16"}
                    wrapperColor="transparent"
                    className={css({transform: "rotate(-45deg)", position: "absolute", top: 0, left: isMobile ? 0 : 48})}
                />
            )}
            {direction === "down-right" && (
                <ChevronDownIcon
                    size={isMobile ? "9" : "16"}
                    wrapperSize={isMobile ? "9" : "16"}
                    wrapperColor="transparent"
                    className={css({transform: "rotate(-45deg)", position: "absolute", bottom: isMobile ? 0 : 48, right: isMobile ? 0 : 48})}
                />
            )}
        </>
    );
};

const closeButton = css({
    position: "absolute",
    backgroundColor: "#fff",
    right: 2,
    top: 2,
    borderRadius: "100%",
    padding: 2,

    lg: {
        padding: 1
    }
});

const modalWrapper = css({
    position: "relative",
    maxHeight: "none",
    width: "100%",
    maxWidth: "1000px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    lg: {
        borderRadius: 2,
        maxHeight: "640px",
        height: "100%"
    }
});

const gridWrap = css({
    overflowY: "auto",
    height: "100%",
    width: "100%"
});

const leftPanel = css({
    overflow: "hidden",
    position: "relative",

    lg: {
        height: "100%"
    }
});

const leftCol = css({
    display: "flex",
    flexDirection: "column",
    height: "100%"
});

const sliderViewport = css({
    position: "relative",
    overflow: "hidden",
    width: "100%",
    height: "100%"
});

const sliderTrack = css({
    display: "flex",
    width: "100%",
    height: "100%",
    transition: "transform 300ms ease-in-out",
    willChange: "transform",

    "@media (prefers-reduced-motion: reduce)": {
        transition: "none"
    }
});

const slideItem = css({
    position: "relative",
    flex: "0 0 100%",
    height: "100%",
    display: "flex"
});

const modalContent = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 2,
    height: "100%",
    width: "100%",
    padding: 4,
    paddingY: 94,

    lg: {
        justifyContent: "center",
        gap: 1.5,
        paddingY: 0
    }
});

const controlsRow = css({
    position: "absolute",
    bottom: 24,
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    alignItems: "center",
    gap: 2
});

const navBtn = css({
    appearance: "none",
    background: "transparent",
    border: "none",
    padding: 0,
    cursor: "pointer"
});
import {useRef, useState} from "react";
import {css, cva, cx} from "@panda/css";

import {BrandCellPhoneIcon, BrandCoinIcon, BrandTenementIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon} from "@pg-design/icons-module";
import {Modal} from "@pg-design/modal-module";
import {getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {useUserDevice} from "@pg-mono/user-device";

interface IProps {
    closeModal: () => void;
    isOpen: boolean;
}

type Slide = {
    title: string;
    body: React.ReactNode;
    leftBg: string;
    icon?: React.ElementType;
    size?: string;
};

const slides: Slide[] = [
    {
        title: "Wszystkie ceny ofert widoczne po raz pierwszy w jednym miejscu!",
        body: <>Dotąd część ofert ukrywała ceny. Teraz zobaczysz je wszystkie w jednym miejscu. Dzięki temu łatwiej porównasz oferty i zaplanujesz budżet.</>,
        leftBg: "var(--colors-info)",
        icon: BrandCoinIcon
    },
    {
        title: "Inteligentne rekomendacje",
        body: (
            <>
                Nasz system wyszuka i wybierze mieszkania dopasowane do Twoich potrzeb. To sprawi, że szybciej dotrzesz do ofert, które naprawdę Ci odpowiadają.
            </>
        ),
        leftBg: "var(--colors-warning)",
        icon: BrandTenementIcon
    },
    {
        title: "Twoje Konto",
        body: (
            <>
                By pomóc Ci w poszukiwaniach, stworzyliśmy dla Ciebie jedną przestrzeń, gdzie możesz wygodnie śledzić i porównywać oferty dopasowane do Twoich
                preferencji oraz komunikować się z deweloperami.
            </>
        ),
        leftBg: "var(--colors-highlight)",
        icon: BrandCellPhoneIcon
    }
];

export const PriceTransparencyModal = ({closeModal, isOpen}: IProps) => {
    const [idx, setIdx] = useState(0);
    const trackRef = useRef<HTMLDivElement | null>(null);
    const {isMobile} = useUserDevice();

    const goTo = (i: number) => {
        setIdx(i);
    };

    const prev = () => goTo((idx - 1 + slides.length) % slides.length);
    const next = () => goTo((idx + 1) % slides.length);

    const slide = slides[idx];

    return (
        <Modal isOpen={isOpen} onModalClose={closeModal} className={modalWrapper}>
            <Modal.CloseButton className={closeButton} placement="right" wrapperColor="#fff" size={isMobile ? "2" : "1.5"} />

            <div className={gridWrap}>
                <div className={leftPanel} style={{backgroundColor: slide.leftBg}}>
                    <div className={leftCol}>
                        <div className={sliderViewport}>
                            <div ref={trackRef} className={sliderTrack} style={{transform: `translateX(-${idx * 100}%)`}}>
                                {slides.map((item, i) => (
                                    <div key={i} className={slideItem}>
                                        <BrandArrowAddon direction="up-left" />

                                        <div className={modalContent}>
                                            {item.icon && (
                                                <item.icon
                                                    size={isMobile ? "5.7" : "9.4"}
                                                    wrapperSize={isMobile ? "5.7" : "9.4"}
                                                    wrapperColor={getThemeVariable("colors.primary")}
                                                />
                                            )}

                                            <Text variant="headline_1" align="center" className={css({maxWidth: "521px"})}>
                                                {item.title}
                                            </Text>

                                            <Text variant="headline_4" align="center" className={css({maxWidth: "628px"})}>
                                                {item.body}
                                            </Text>
                                        </div>

                                        <BrandArrowAddon direction="down-right" />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <ModalControls
                            dotSize={isMobile ? "small" : "medium"}
                            arrowSize={isMobile ? "1.5" : "2.5"}
                            prev={prev}
                            next={next}
                            idx={idx}
                            setIdx={goTo}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

interface IModalControlsProps {
    prev: () => void;
    next: () => void;
    idx: number;
    setIdx: (idx: number) => void;
    arrowSize: string;
    dotSize?: "small" | "medium";
}

const ModalControls = ({prev, next, idx, setIdx, arrowSize, dotSize = "medium"}: IModalControlsProps) => {
    return (
        <div className={controlsRow}>
            <button type="button" className={navBtn} onClick={prev} aria-label="Poprzedni">
                <ChevronLeftIcon size={arrowSize} fill="#000" wrapperColor="#fff" wrapperSize="5" />
            </button>

            <div className={css({display: "flex", gap: 16})}>
                {slides.map((_, i) => (
                    <button
                        key={i}
                        className={cx(dot({active: i === idx, dotSize: dotSize}))}
                        aria-label={`Przejdź do slajdu ${i + 1}`}
                        onClick={() => setIdx(i)}
                    />
                ))}
            </div>

            <button type="button" className={navBtn} onClick={next} aria-label="Następny">
                <ChevronRightIcon size={arrowSize} fill="#000" wrapperColor="#fff" wrapperSize="5" />
            </button>
        </div>
    );
};

const dot = cva({
    base: {
        borderRadius: "100%",
        backgroundColor: "var(--colors-gray-500)",
        cursor: "pointer"
    },
    variants: {
        active: {
            true: {
                backgroundColor: "var(--colors-primary)"
            }
        },
        dotSize: {
            small: {
                height: 10,
                width: 10
            },
            medium: {
                height: 16,
                width: 16
            }
        }
    }
});

const BrandArrowAddon = ({direction}: {direction: "up-left" | "down-right"}) => {
    const {isMobile} = useUserDevice();

    return (
        <>
            {direction === "up-left" && (
                <ChevronUpIcon
                    size={isMobile ? "9" : "16"}
                    wrapperSize={isMobile ? "9" : "16"}
                    wrapperColor="transparent"
                    className={css({transform: "rotate(-45deg)", position: "absolute", top: 0, left: isMobile ? 0 : 48})}
                />
            )}
            {direction === "down-right" && (
                <ChevronDownIcon
                    size={isMobile ? "9" : "16"}
                    wrapperSize={isMobile ? "9" : "16"}
                    wrapperColor="transparent"
                    className={css({transform: "rotate(-45deg)", position: "absolute", bottom: isMobile ? 0 : 48, right: isMobile ? 0 : 48})}
                />
            )}
        </>
    );
};

const closeButton = css({
    position: "absolute",
    backgroundColor: "#fff",
    right: 2,
    top: 2,
    borderRadius: "100%",
    padding: 2,

    lg: {
        padding: 1
    }
});

const modalWrapper = css({
    position: "relative",
    maxHeight: "none",
    width: "100%",
    maxWidth: "1000px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    lg: {
        borderRadius: 2,
        maxHeight: "640px",
        height: "100%"
    }
});

const gridWrap = css({
    overflowY: "auto",
    height: "100%",
    width: "100%"
});

const leftPanel = css({
    overflow: "hidden",
    position: "relative",

    lg: {
        height: "100%"
    }
});

const leftCol = css({
    display: "flex",
    flexDirection: "column",
    height: "100%"
});

const sliderViewport = css({
    position: "relative",
    overflow: "hidden",
    width: "100%",
    height: "100%"
});

const sliderTrack = css({
    display: "flex",
    width: "100%",
    height: "100%",
    transition: "transform 300ms ease-in-out",
    willChange: "transform",

    "@media (prefers-reduced-motion: reduce)": {
        transition: "none"
    }
});

const slideItem = css({
    position: "relative",
    flex: "0 0 100%",
    height: "100%",
    display: "flex"
});

const modalContent = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 2,
    height: "100%",
    width: "100%",
    padding: 4,
    paddingY: 94,

    lg: {
        justifyContent: "center",
        gap: 1.5,
        paddingY: 0
    }
});

const controlsRow = css({
    position: "absolute",
    bottom: 24,
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    alignItems: "center",
    gap: 2
});

const navBtn = css({
    appearance: "none",
    background: "transparent",
    border: "none",
    padding: 0,
    cursor: "pointer"
});
